export type SplitName = {
  firstName: string;
  lastName: string;
};

/**
 * Splits a full name into a first and last name for prefilling the contact form.
 *
 * Reproduces the reference selector's own `split_name`, which is the caller that
 * supplies the `name` query parameter. A name containing Japanese, Chinese or
 * Korean characters is not split: family-name-first order and the absence of word
 * breaks make an automatic split unreliable, so the whole value becomes the last
 * name. A single word is treated the same way.
 *
 * This is a prefill, not authoritative — the editor can correct either field.
 */
export function splitName(full: string | null | undefined): SplitName {
  const normalised = (full ?? "").trim().replace(/\s+/g, " ");

  if (!normalised) {
    return { firstName: "", lastName: "" };
  }

  if (containsUnsplittableScript(normalised)) {
    return { firstName: "", lastName: normalised };
  }

  const firstSpace = normalised.indexOf(" ");

  if (firstSpace === -1) {
    return { firstName: "", lastName: normalised };
  }

  return {
    firstName: normalised.slice(0, firstSpace),
    lastName: normalised.slice(firstSpace + 1),
  };
}

/**
 * Hiragana and Katakana, CJK Extension A, CJK Unified Ideographs, CJK
 * Compatibility Ideographs, and Hangul syllables. Characters above the Basic
 * Multilingual Plane are deliberately not covered — see DEV-2769.
 */
function containsUnsplittableScript(value: string): boolean {
  return /[぀-ヿ㐀-䶿一-鿿豈-﫿가-힯]/.test(value);
}
